import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  CompleteWhatsAppEmbeddedSignupPendingResponse,
  CompleteWhatsAppEmbeddedSignupResponse,
  WhatsAppConnectionResponse,
} from "@multi-chat/shared";
import { isApiError, isBackendUnavailable, type ApiError } from "../../api/client.js";
import { verificationCooldownCopy } from "./add-phone/add-phone-errors.js";
import {
  cancelPending as cancelPendingApi,
  completeEmbeddedSignup as completeSignupApi,
  disconnect as disconnectApi,
  getConnection as getConnectionApi,
  selectPhoneNumbers as selectPhonesApi,
  switchWorkspacePhone as switchWorkspacePhoneApi,
} from "../../api/whatsapp-api.js";
import { withPhoneProfilePicture } from "./whatsapp-phone-avatar.js";

export type WhatsAppView =
  | { kind: "loading" }
  | { kind: "unavailable"; message: string }
  | { kind: "ready"; connection: WhatsAppConnectionResponse }
  | { kind: "pending-selection"; candidates: CompleteWhatsAppEmbeddedSignupPendingResponse }
  | { kind: "error"; message: string; code?: string };

interface UseWhatsAppConnectionOptions {
  onConnectionUpdated?: (connection: WhatsAppConnectionResponse) => void;
  /** When true, a connection reload must not replace the open wizard or show a skeleton. */
  holdConnectionView?: () => boolean;
}

interface UseWhatsAppConnectionResult {
  view: WhatsAppView;
  refresh: () => void;
  refreshQuietly: () => Promise<void>;
  applyPhoneProfilePicture: (
    externalPhoneNumberId: string,
    profilePictureUrl: string | null,
  ) => void;
  submitSignup: (payload: {
    authorizationCode: string;
    wabaId: string;
    metaBusinessId?: string;
    phoneNumberId?: string;
    signupEvent?: string;
  }) => Promise<void>;
  submitPhoneSelection: (externalPhoneNumberIds: string[]) => Promise<void>;
  acceptConnection: (connection: WhatsAppConnectionResponse) => void;
  switchWorkspacePhone: (externalPhoneNumberId: string) => Promise<void>;
  cancelPending: () => Promise<void>;
  disconnect: () => Promise<void>;
  busy: boolean;
  actionError: string | null;
  clearActionError: () => void;
}

export function useWhatsAppConnection(
  organizationId: number,
  unreachableMessage: string,
  options: UseWhatsAppConnectionOptions = {},
): UseWhatsAppConnectionResult {
  const { t, i18n } = useTranslation();
  const [view, setView] = useState<WhatsAppView>({ kind: "loading" });
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const loadIdRef = useRef(0);
  const signupGenerationRef = useRef(0);
  const preserveSignupViewRef = useRef(false);
  const preserveSignupOrgRef = useRef<number | null>(null);
  const orgIdRef = useRef(organizationId);
  orgIdRef.current = organizationId;
  const onConnectionUpdatedRef = useRef(options.onConnectionUpdated);
  onConnectionUpdatedRef.current = options.onConnectionUpdated;
  const holdConnectionViewRef = useRef(options.holdConnectionView);
  holdConnectionViewRef.current = options.holdConnectionView;
  const unreachableRef = useRef(unreachableMessage);
  unreachableRef.current = unreachableMessage;
  const viewRef = useRef(view);
  viewRef.current = view;
  const viewOrgRef = useRef(organizationId);

  const clearActionError = useCallback(() => setActionError(null), []);

  const refresh = useCallback(() => {
    preserveSignupViewRef.current = false;
    preserveSignupOrgRef.current = null;
    setReloadToken((token) => token + 1);
  }, []);

  const applyPhoneProfilePicture = useCallback(
    (externalPhoneNumberId: string, profilePictureUrl: string | null) => {
      setView((current) => {
        if (current.kind !== "ready") return current;
        return {
          kind: "ready",
          connection: withPhoneProfilePicture(
            current.connection,
            externalPhoneNumberId,
            profilePictureUrl,
          ),
        };
      });
    },
    [],
  );

  const refreshQuietly = useCallback(async () => {
    const currentOrgId = orgIdRef.current;
    const connection = await getConnectionApi(currentOrgId);
    setView((current) => {
      if (current.kind !== "ready") return current;
      return { kind: "ready", connection };
    });
    onConnectionUpdatedRef.current?.(connection);
  }, []);

  const localizedApiMessage = useCallback(
    (error: ApiError): string => {
      switch (error.code) {
        case "WHATSAPP_EMBEDDED_SIGNUP_VERIFICATION_INCOMPLETE":
          return t("whatsapp.meta.verificationIncomplete");
        case "WHATSAPP_WABA_OWNED_BY_ANOTHER_ORGANIZATION":
          return t("whatsapp.meta.wabaOwnedByAnotherOrganization");
        case "WHATSAPP_PHONE_OWNED_BY_ANOTHER_ORGANIZATION":
          return t("whatsapp.addPhone.errors.ownedByAnotherOrganization");
        case "WHATSAPP_PROVIDER_ERROR":
          return t("whatsapp.meta.providerError");
        case "WHATSAPP_PHONE_ALREADY_EXISTS":
          return t("whatsapp.addPhone.errors.phoneAlreadyExists");
        case "WHATSAPP_VERIFICATION_RATE_LIMITED":
          return error.details?.retryAfter
            ? verificationCooldownCopy(t, {
                retryAfter: error.details.retryAfter,
                retryAfterSource: error.details.retryAfterSource,
                language: i18n.language,
              })
            : t("whatsapp.addPhone.errors.rateLimited");
        case "WHATSAPP_VERIFICATION_CODE_INVALID":
          return t("whatsapp.addPhone.errors.invalidCode");
        case "WHATSAPP_VERIFICATION_CODE_EXPIRED":
          return t("whatsapp.addPhone.errors.expiredCode");
        case "WHATSAPP_VERIFICATION_REQUEST_FAILED":
          return t("whatsapp.addPhone.errors.requestFailed");
        case "WHATSAPP_REGISTRATION_FAILED":
          return t("whatsapp.addPhone.errors.registrationFailed");
        default:
          return t("whatsapp.error.message");
      }
    },
    [i18n.language, t],
  );

  const toErrorView = useCallback((error: unknown): WhatsAppView => {
    if (isBackendUnavailable(error)) {
      return { kind: "unavailable", message: unreachableRef.current };
    }
    if (isApiError(error)) {
      return { kind: "error", message: localizedApiMessage(error), code: error.code };
    }
    return { kind: "unavailable", message: unreachableRef.current };
  }, [localizedApiMessage]);

  const toActionError = useCallback((error: unknown): string => {
    if (isBackendUnavailable(error)) return unreachableRef.current;
    if (isApiError(error)) return localizedApiMessage(error);
    return unreachableRef.current;
  }, [localizedApiMessage]);

  useEffect(() => {
    if (
      preserveSignupViewRef.current &&
      preserveSignupOrgRef.current === organizationId
    ) {
      return;
    }
    const keepPendingSelection =
      viewRef.current.kind === "pending-selection" && viewOrgRef.current === organizationId;
    if (holdConnectionViewRef.current?.() === true || keepPendingSelection) {
      return;
    }
    preserveSignupViewRef.current = false;
    preserveSignupOrgRef.current = null;

    const controller = new AbortController();
    const loadId = ++loadIdRef.current;
    const currentOrgId = orgIdRef.current;
    viewOrgRef.current = currentOrgId;
    setView({ kind: "loading" });
    setActionError(null);

    (async () => {
      try {
        const connection = await getConnectionApi(currentOrgId, controller.signal);
        if (loadId !== loadIdRef.current) return;
        if (
          preserveSignupViewRef.current &&
          preserveSignupOrgRef.current === currentOrgId
        ) {
          return;
        }
        if (holdConnectionViewRef.current?.() === true) return;
        if (
          viewRef.current.kind === "pending-selection" &&
          viewOrgRef.current === currentOrgId
        ) {
          return;
        }
        viewOrgRef.current = currentOrgId;
        setView({ kind: "ready", connection });
        onConnectionUpdatedRef.current?.(connection);
      } catch (error) {
        if (controller.signal.aborted || loadId !== loadIdRef.current) return;
        if (
          preserveSignupViewRef.current &&
          preserveSignupOrgRef.current === currentOrgId
        ) {
          return;
        }
        if (holdConnectionViewRef.current?.() === true) return;
        setView(toErrorView(error));
      }
    })();

    return () => controller.abort();
  }, [organizationId, reloadToken, toErrorView]);

  const submitSignup = useCallback(
    async (payload: {
      authorizationCode: string;
      wabaId: string;
      metaBusinessId?: string;
      phoneNumberId?: string;
      signupEvent?: string;
    }) => {
      const generation = ++signupGenerationRef.current;
      const orgId = orgIdRef.current;
      ++loadIdRef.current;
      preserveSignupViewRef.current = true;
      preserveSignupOrgRef.current = orgId;
      setBusy(true);
      setActionError(null);
      try {
        const result = await completeSignupApi(orgId, payload);
        if (generation !== signupGenerationRef.current || orgId !== orgIdRef.current) return;
        preserveSignupViewRef.current = true;
        preserveSignupOrgRef.current = orgId;
        if (result.state === "CONNECTED") {
          viewOrgRef.current = orgId;
          setView({ kind: "ready", connection: result.connection });
          onConnectionUpdatedRef.current?.(result.connection);
        } else {
          viewOrgRef.current = orgId;
          setView({ kind: "pending-selection", candidates: result });
        }
      } catch (error) {
        if (generation !== signupGenerationRef.current || orgId !== orgIdRef.current) return;
        preserveSignupViewRef.current = true;
        preserveSignupOrgRef.current = orgId;
        setView(toErrorView(error));
      } finally {
        if (generation === signupGenerationRef.current) setBusy(false);
      }
    },
    [toErrorView],
  );

  const acceptConnection = useCallback((connection: WhatsAppConnectionResponse) => {
    ++loadIdRef.current;
    preserveSignupViewRef.current = false;
    preserveSignupOrgRef.current = null;
    setView({ kind: "ready", connection });
    onConnectionUpdatedRef.current?.(connection);
  }, []);

  const submitPhoneSelection = useCallback(
    async (externalPhoneNumberIds: string[]) => {
      setBusy(true);
      setActionError(null);
      try {
        const connection = await selectPhonesApi(orgIdRef.current, externalPhoneNumberIds);
        setView({ kind: "ready", connection });
        onConnectionUpdatedRef.current?.(connection);
      } catch (error) {
        setActionError(toActionError(error));
      } finally {
        setBusy(false);
      }
    },
    [toActionError],
  );

  const switchWorkspacePhone = useCallback(
    async (externalPhoneNumberId: string) => {
      setBusy(true);
      setActionError(null);
      try {
        const connection = await switchWorkspacePhoneApi(
          orgIdRef.current,
          externalPhoneNumberId,
        );
        setView({ kind: "ready", connection });
        onConnectionUpdatedRef.current?.(connection);
      } catch (error) {
        setActionError(toActionError(error));
        throw error;
      } finally {
        setBusy(false);
      }
    },
    [toActionError],
  );

  const cancelPending = useCallback(async () => {
    setBusy(true);
    setActionError(null);
    try {
      const connection = await cancelPendingApi(orgIdRef.current);
      setView({ kind: "ready", connection });
      onConnectionUpdatedRef.current?.(connection);
    } catch (error) {
      setActionError(toActionError(error));
    } finally {
      setBusy(false);
    }
  }, [toActionError]);

  const disconnect = useCallback(async () => {
    setBusy(true);
    setActionError(null);
    try {
      const connection = await disconnectApi(orgIdRef.current);
      setView({ kind: "ready", connection });
      onConnectionUpdatedRef.current?.(connection);
    } catch (error) {
      setActionError(toActionError(error));
    } finally {
      setBusy(false);
    }
  }, [toActionError]);

  return {
    view,
    refresh,
    refreshQuietly,
    applyPhoneProfilePicture,
    submitSignup,
    submitPhoneSelection,
    acceptConnection,
    switchWorkspacePhone,
    cancelPending,
    disconnect,
    busy,
    actionError,
    clearActionError,
  };
}
